/**
 * Workspace boost usage component for the admin workspace detail "Usage" tab.
 * Shows boost spending, provider costs, model/feature breakdowns, member balances, and transactions.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Zap,
  DollarSign,
  Wallet,
  AlertCircle,
  Loader2,
  ChevronRight,
  Plus,
  Check,
} from "lucide-react";
import {
  useAdminWorkspaceBoostUsage,
  useAdminWorkspaceBoostTransactions,
  useAdminWorkspaceCollaborators,
  useAdminGrantBoosts,
} from "../../hooks/useAdminWorkspaces";
import FilterSelect from "../../components/FilterSelect";
import { collaboratorName } from "../../lib/collaborators";
import { WorkspaceContractCard } from "../../components/admin/WorkspaceContractCard";
import {
  sttWorkflowLabel,
  txDescription,
  txWorkflowLabel,
  workflowOptions,
  workflowParams,
  formatAudioDuration,
  ledgerTypeKey,
  applyTxFilter,
  EMPTY_TX_FILTERS,
  BOOST_TRANSACTION_TYPES,
  type TxFilters,
} from "../../lib/boostLedger";

interface WorkspaceBoostUsageProps {
  workspaceId: string;
}

function formatCurrency(value: number): string {
  return `$${value.toFixed(2)}`;
}

function formatNumber(value: number): string {
  return value.toLocaleString();
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function WorkspaceBoostUsage({ workspaceId }: WorkspaceBoostUsageProps) {
  const { t } = useTranslation();
  const [txPage, setTxPage] = useState({ filters: EMPTY_TX_FILTERS, offset: 0 });
  const { filters: txFilters, offset: txOffset } = txPage;
  const [wfFilter, setWfFilter] = useState("");
  const [showGrantForm, setShowGrantForm] = useState(false);
  const [grantAmount, setGrantAmount] = useState("");
  const [grantReason, setGrantReason] = useState("");
  const [grantSuccess, setGrantSuccess] = useState<string | null>(null);
  const TX_LIMIT = 20;
  const setTxFilter = (patch: Partial<TxFilters>) => setTxPage((p) => applyTxFilter(p, patch));

  const {
    data: usage,
    isLoading: usageLoading,
    error: usageError,
  } = useAdminWorkspaceBoostUsage(workspaceId, workflowParams(wfFilter));

  const { data: collaboratorsData } = useAdminWorkspaceCollaborators(workspaceId);
  const members = collaboratorsData?.collaborators ?? [];

  const {
    data: txData,
    isLoading: txLoading,
    error: txError,
  } = useAdminWorkspaceBoostTransactions(
    workspaceId,
    TX_LIMIT,
    txOffset,
    txFilters.startDate || undefined,
    txFilters.endDate || undefined,
    {
      type: txFilters.type || undefined,
      userId: txFilters.userId || undefined,
      ...workflowParams(txFilters.workflow),
    }
  );

  const grantMutation = useAdminGrantBoosts(workspaceId);

  function handleGrant(e: React.FormEvent) {
    e.preventDefault();
    const amount = parseInt(grantAmount, 10);
    if (!amount || !grantReason.trim()) return;

    grantMutation.mutate(
      { amount, reason: grantReason.trim() },
      {
        onSuccess: (data) => {
          setGrantSuccess(`Granted ${amount} boosts. New balance: ${data.newBalance}`);
          setGrantAmount("");
          setGrantReason("");
          setTimeout(() => setGrantSuccess(null), 5000);
        },
      }
    );
  }

  if (usageLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        <span className="ml-2 text-sm text-muted-foreground">Loading usage data...</span>
      </div>
    );
  }

  if (usageError) {
    return (
      <div className="flex items-center gap-3 p-4 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg">
        <AlertCircle className="h-5 w-5 text-red-600 shrink-0" />
        <div>
          <p className="text-sm font-medium text-red-800 dark:text-red-200">Failed to load boost usage</p>
          <p className="text-xs text-red-600">{usageError.message}</p>
        </div>
      </div>
    );
  }

  if (!usage) return null;

  const workflowFilterOptions = workflowOptions(usage.workflowsUsed);

  return (
    <div className="space-y-8">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="p-4 bg-orange-50 dark:bg-orange-950/30 border border-orange-200 dark:border-orange-800 rounded-lg">
          <div className="flex items-center gap-2 mb-1">
            <Zap className="h-4 w-4 text-orange-600" />
            <span className="text-xs font-medium text-orange-700 dark:text-orange-300 uppercase tracking-wide">
              Total Boosts Spent
            </span>
          </div>
          <p className="text-2xl font-bold text-orange-900 dark:text-orange-100">
            {formatNumber(usage.totalBoostsSpent)}
          </p>
        </div>

        <div className="p-4 bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-lg">
          <div className="flex items-center gap-2 mb-1">
            <DollarSign className="h-4 w-4 text-green-600" />
            <span className="text-xs font-medium text-green-700 dark:text-green-300 uppercase tracking-wide">
              Total Provider Cost
            </span>
          </div>
          <p className="text-2xl font-bold text-green-900 dark:text-green-100">
            {formatCurrency(usage.totalProviderCostUsd)}
          </p>
        </div>

        <div className="p-4 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg">
          <div className="flex items-center gap-2 mb-1">
            <Wallet className="h-4 w-4 text-blue-600" />
            <span className="text-xs font-medium text-blue-700 dark:text-blue-300 uppercase tracking-wide">
              Balance
            </span>
          </div>
          <p className="text-2xl font-bold text-blue-900 dark:text-blue-100">
            {formatNumber(usage.workspaceBalance)}
          </p>
        </div>
      </div>

      {/* Grant Boosts */}
      <div>
        {!showGrantForm ? (
          <button
            onClick={() => setShowGrantForm(true)}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 rounded-lg transition-colors"
          >
            <Plus className="h-4 w-4" />
            Grant Boosts
          </button>
        ) : (
          <div className="p-4 border border-orange-200 dark:border-orange-800 bg-orange-50/50 dark:bg-orange-950/20 rounded-lg">
            <h3 className="text-sm font-semibold text-foreground mb-3">Grant Boosts to Workspace</h3>
            <form onSubmit={handleGrant} className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">Amount</label>
                  <input
                    type="number"
                    value={grantAmount}
                    onChange={(e) => setGrantAmount(e.target.value)}
                    placeholder="e.g. 100"
                    min={1}
                    max={10000}
                    className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">Reason</label>
                  <input
                    type="text"
                    value={grantReason}
                    onChange={(e) => setGrantReason(e.target.value)}
                    placeholder="e.g. Monthly allocation"
                    className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background"
                    required
                  />
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="submit"
                  disabled={grantMutation.isPending}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 disabled:opacity-50 rounded-lg transition-colors"
                >
                  {grantMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Zap className="h-4 w-4" />
                  )}
                  Grant
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowGrantForm(false);
                    setGrantAmount("");
                    setGrantReason("");
                    grantMutation.reset();
                  }}
                  className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  Cancel
                </button>
                {grantSuccess && (
                  <span className="inline-flex items-center gap-1 text-sm text-green-600">
                    <Check className="h-4 w-4" />
                    {grantSuccess}
                  </span>
                )}
                {grantMutation.isError && (
                  <span className="text-sm text-red-600">
                    {grantMutation.error.message}
                  </span>
                )}
              </div>
            </form>
          </div>
        )}
      </div>

      <WorkspaceContractCard workspaceId={workspaceId} />

      {/* Service Breakdown */}
      {usage.operations.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-foreground mb-3">Service Breakdown</h3>
          <div className="border border-border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted/50">
                  <th className="text-left px-4 py-2 font-medium text-muted-foreground">Service</th>
                  <th className="text-left px-4 py-2 font-medium text-muted-foreground">Processor</th>
                  <th className="text-right px-4 py-2 font-medium text-muted-foreground">Boosts Charged</th>
                  <th className="text-right px-4 py-2 font-medium text-muted-foreground">Provider Cost</th>
                  <th className="text-right px-4 py-2 font-medium text-muted-foreground">Txns</th>
                </tr>
              </thead>
              <tbody>
                {usage.operations.map((op) => (
                  <tr key={`${op.service}-${op.processor}`} className="border-t border-border">
                    <td className="px-4 py-2 font-medium">{op.service}</td>
                    <td className="px-4 py-2 text-muted-foreground">{op.processor}</td>
                    <td className="px-4 py-2 text-right font-medium">{formatNumber(op.totalBoostsCharged)}</td>
                    <td className="px-4 py-2 text-right text-muted-foreground">{formatCurrency(op.providerCostUsd)}</td>
                    <td className="px-4 py-2 text-right text-muted-foreground">{formatNumber(op.transactionCount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Recent Workflows */}
      {(usage.workflows.length > 0 || wfFilter) && (
        <div>
          <h3 className="text-sm font-semibold text-foreground mb-3">Recent Workflows</h3>
          <div className="mb-4">
            <FilterSelect
              label={t("billing.ledger.filters.workflow")}
              value={wfFilter}
              onChange={setWfFilter}
              allLabel={t("billing.ledger.filters.allWorkflows")}
              options={workflowFilterOptions}
            />
          </div>
          <div className="border border-border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted/50">
                  <th className="text-left px-4 py-2 font-medium text-muted-foreground">Date</th>
                  <th className="text-left px-4 py-2 font-medium text-muted-foreground">Document</th>
                  <th className="text-left px-4 py-2 font-medium text-muted-foreground">{t("billing.ledger.filters.workflow")}</th>
                  <th className="text-right px-4 py-2 font-medium text-muted-foreground">Audio</th>
                  <th className="text-right px-4 py-2 font-medium text-muted-foreground">Images</th>
                  <th className="text-right px-4 py-2 font-medium text-muted-foreground">Total Boosts</th>
                  <th className="text-right px-4 py-2 font-medium text-muted-foreground">Boosts/min</th>
                  <th className="text-right px-4 py-2 font-medium text-muted-foreground">Provider Cost</th>
                  <th className="text-right px-4 py-2 font-medium text-muted-foreground">Cost/min</th>
                  <th className="text-right px-4 py-2 font-medium text-muted-foreground">Txns</th>
                </tr>
              </thead>
              <tbody>
                {usage.workflows.map((wf) => (
                  <tr key={wf.naraFlowId} className="border-t border-border">
                    <td className="px-4 py-2 text-muted-foreground whitespace-nowrap">
                      {wf.createdAt ? formatDate(wf.createdAt) : "—"}
                    </td>
                    <td className="px-4 py-2 font-mono text-xs text-muted-foreground truncate max-w-[200px]">
                      {wf.documentId}
                    </td>
                    <td className="px-4 py-2 text-muted-foreground whitespace-nowrap">
                      {wf.workflowLabel || sttWorkflowLabel(wf.stt_model)}
                    </td>
                    <td className="px-4 py-2 text-right text-muted-foreground">{formatAudioDuration(wf.audioDurationSec)}</td>
                    <td className="px-4 py-2 text-right text-muted-foreground">{formatNumber(wf.imageCount ?? 0)}</td>
                    <td className="px-4 py-2 text-right font-medium">{formatNumber(wf.totalBoostsCharged)}</td>
                    <td className="px-4 py-2 text-right text-muted-foreground">{wf.boostsPerMinute?.toFixed(1) ?? "—"}</td>
                    <td className="px-4 py-2 text-right text-muted-foreground">{formatCurrency(wf.providerCostUsd)}</td>
                    <td className="px-4 py-2 text-right text-muted-foreground">
                      {wf.providerCostPerMinute == null ? "—" : `$${wf.providerCostPerMinute.toFixed(4)}`}
                    </td>
                    <td className="px-4 py-2 text-right text-muted-foreground">{formatNumber(wf.transactionCount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Recent Transactions */}
      <div>
        <h3 className="text-sm font-semibold text-foreground mb-3">Recent Transactions</h3>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <FilterSelect
            label={t("billing.ledger.filters.type")}
            value={txFilters.type}
            onChange={(type) => setTxFilter({ type })}
            allLabel={t("billing.ledger.filters.allTypes")}
            options={BOOST_TRANSACTION_TYPES.map((v) => ({ value: v, label: t(`billing.ledger.types.${v}`) }))}
          />
          <FilterSelect
            label={t("billing.ledger.filters.member")}
            value={txFilters.userId}
            onChange={(userId) => setTxFilter({ userId })}
            allLabel={t("billing.ledger.filters.allMembers")}
            options={members.map((c) => ({ value: c.user_id, label: collaboratorName(c) }))}
          />
          <FilterSelect
            label={t("billing.ledger.filters.workflow")}
            value={txFilters.workflow}
            onChange={(workflow) => setTxFilter({ workflow })}
            allLabel={t("billing.ledger.filters.allWorkflows")}
            options={workflowFilterOptions}
          />
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">Start Date</label>
            <input
              type="date"
              value={txFilters.startDate}
              onChange={(e) => setTxFilter({ startDate: e.target.value })}
              className="px-3 py-1.5 text-sm border border-border rounded-lg bg-background"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">End Date</label>
            <input
              type="date"
              value={txFilters.endDate}
              onChange={(e) => setTxFilter({ endDate: e.target.value })}
              className="px-3 py-1.5 text-sm border border-border rounded-lg bg-background"
            />
          </div>
          {(txFilters.startDate || txFilters.endDate) && (
            <button
              onClick={() => setTxFilter({ startDate: "", endDate: "" })}
              className="mt-4 text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              Clear
            </button>
          )}
        </div>
        {txLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : txError ? (
          <div className="flex items-center gap-3 p-4 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg">
            <AlertCircle className="h-5 w-5 text-red-600 shrink-0" />
            <p className="text-sm text-red-800 dark:text-red-200">{txError.message}</p>
          </div>
        ) : !txData?.transactions.length ? (
          <p className="text-sm text-muted-foreground py-4">No transactions found.</p>
        ) : (
          <>
            <div className="border border-border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted/50">
                    <th className="text-left px-4 py-2 font-medium text-muted-foreground">Date</th>
                    <th className="text-left px-4 py-2 font-medium text-muted-foreground">Type</th>
                    <th className="text-left px-4 py-2 font-medium text-muted-foreground">Description</th>
                    <th className="text-left px-4 py-2 font-medium text-muted-foreground">{t("billing.ledger.filters.workflow")}</th>
                    <th className="text-right px-4 py-2 font-medium text-muted-foreground">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {txData.transactions.map((tx) => (
                    <tr key={tx.id} className="border-t border-border">
                      <td className="px-4 py-2 text-muted-foreground whitespace-nowrap">
                        {formatDate(tx.created_at)}
                      </td>
                      <td className="px-4 py-2">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                          tx.amount > 0
                            ? "bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300"
                            : "bg-orange-100 dark:bg-orange-900 text-orange-700 dark:text-orange-300"
                        }`}>
                          {ledgerTypeKey(tx.type) ? t(ledgerTypeKey(tx.type)!) : tx.type}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-muted-foreground truncate max-w-[200px]">
                        {txDescription(tx)}
                      </td>
                      <td className="px-4 py-2 text-muted-foreground whitespace-nowrap">
                        {txWorkflowLabel(tx.workflowLabel, tx.stt_model)}
                      </td>
                      <td className={`px-4 py-2 text-right font-medium ${
                        tx.amount > 0 ? "text-green-600" : "text-orange-600"
                      }`}>
                        {tx.amount > 0 ? "+" : ""}{formatNumber(tx.amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {(txOffset > 0 || txData.hasMore) && (
              <div className="mt-3 flex items-center justify-between">
                <button
                  onClick={() => setTxPage((p) => ({ ...p, offset: Math.max(0, txOffset - TX_LIMIT) }))}
                  disabled={txOffset === 0}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-foreground border border-border rounded-lg hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Previous
                </button>
                <span className="text-xs text-muted-foreground">
                  Showing {txOffset + 1}–{txOffset + txData.transactions.length}
                </span>
                <button
                  onClick={() => setTxPage((p) => ({ ...p, offset: txOffset + TX_LIMIT }))}
                  disabled={!txData.hasMore}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-foreground border border-border rounded-lg hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                  <ChevronRight className="h-3 w-3" />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
